import { ReactNode } from 'react';
import { Box, Stack, SxProps } from '@mui/material';
import {
  SystemCssProperties,
  SystemStyleObject,
} from '@mui/system/styleFunctionSx/styleFunctionSx';

import { Typography } from '@occc-app/design-system';

import { getTimeLabel } from './utils';

import {
  CalendarGridDayOverlappedTimeLineItem,
  CalendarGridDayTimeLineItem,
  CalendarGridTimeMode,
} from '../../types';
import { CALENDAR_GRID_DEFAULTS } from '../../constants';
import { normaliseSx } from '../../utils';

type CalendarGridDayInfoProps = {
  item: CalendarGridDayOverlappedTimeLineItem;
  timeMode: CalendarGridTimeMode;
  onClick?: (item: CalendarGridDayTimeLineItem) => void;
  renderAction?: (item: CalendarGridDayTimeLineItem, textColor?: string) => ReactNode;
  arrowHorizontalPosition?: 'left' | 'right';
  arrowVerticalPosition?: 'top' | 'bottom';
  arrowOffset?: number;
  sx?: SxProps;
};

export const CalendarGridDayInfo = ({
  item,
  timeMode,
  onClick,
  renderAction,
  arrowHorizontalPosition = 'left',
  arrowVerticalPosition = 'top',
  arrowOffset = 0,
  sx,
}: CalendarGridDayInfoProps) => {
  const { footer, subFooter, overlappedItems, startTime, endTime, header } = item;
  const hasOverlappedItems = !!overlappedItems?.length;

  return (
    <Stack
      sx={[
        containerStyle,
        ...normaliseSx(sx),
        (theme) => {
          const size = theme.spacing(1);
          const currentArrowOffset = Math.max(arrowOffset, parseInt(theme.spacing(1.3), 10));
          return {
            boxShadow: 3,
            '&::after': {
              content: '""',
              position: 'absolute',
              borderTop: `${size} solid transparent`,
              borderBottom: `${size} solid transparent`,
              ...(arrowVerticalPosition === 'top'
                ? { top: currentArrowOffset, transform: 'translateY(-50%)' }
                : { bottom: currentArrowOffset, transform: 'translateY(50%)' }),
              ...(arrowHorizontalPosition === 'left'
                ? {
                    left: `-${size}`,
                    borderRight: `${size} solid ${theme.palette.background.paper}`,
                    filter: 'drop-shadow(-1px 0 0 rgba(0,0,0,0.3))',
                  }
                : {
                    right: `-${size}`,
                    borderLeft: `${size} solid ${theme.palette.background.paper}`,
                    filter: 'drop-shadow(1px 0 0 rgba(0,0,0,0.3))',
                  }),
            },
          };
        },
      ]}
      borderRadius={1}
    >
      {hasOverlappedItems ? (
        <Box
          display='flex'
          flexDirection='column'
          flex={1}
          gap={1}
          px={1}
          pt={1}
          mb={3}
          sx={listStyle}
        >
          {overlappedItems.map((overlappedItem) => {
            const { id, timeLaneIndex, startTime, endTime, header } = overlappedItem;

            return (
              <Box
                py={1}
                px={1}
                key={`${id}-${timeLaneIndex}`}
                borderBottom={1}
                borderColor='rgba(0,0,0,0.1)'
                onClick={onClick ? () => onClick(overlappedItem) : undefined}
                sx={onClick ? { cursor: 'pointer' } : undefined}
              >
                <Typography variant='body2' sx={textStyle}>
                  {getTimeLabel(startTime, endTime, timeMode)}
                </Typography>
                {!!header && (
                  <Typography variant='body2' fontWeight={700} sx={textStyle}>
                    {header}
                  </Typography>
                )}
                {renderAction && (
                  <Box display='flex' flexDirection='column' pt={1}>
                    {renderAction(overlappedItem)}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box display='flex' flexDirection='column' p={1.5} gap={0.5}>
          <Typography variant='body2' sx={textStyle}>
            {getTimeLabel(startTime, endTime, timeMode)}
          </Typography>
          {!!header && (
            <Typography variant='body2' fontWeight={700} sx={textStyle}>
              {header}
            </Typography>
          )}
          {renderAction && (
            <Box display='flex' flexDirection='column' pt={1}>
              {renderAction(item)}
            </Box>
          )}
        </Box>
      )}
      {(footer || subFooter) && (
        <Box display='flex' flexDirection='column' p={1} pb={1}>
          {!!footer && (
            <Typography variant='body2' sx={textStyle}>
              {footer}
            </Typography>
          )}
          {!!subFooter && (
            <Typography variant='body2' fontWeight={700} sx={textStyle}>
              {subFooter}
            </Typography>
          )}
        </Box>
      )}
    </Stack>
  );
};

const containerStyle: SystemCssProperties = {
  flex: '1 1 auto',
  minHeight: 0,
  minWidth: `${CALENDAR_GRID_DEFAULTS.dayColumnMinWidth * 1.5}px`,
  justifyContent: 'space-between',
  backgroundColor: CALENDAR_GRID_DEFAULTS.color,
};

const listStyle: SystemCssProperties = {
  overflowY: 'scroll',
};

const textStyle: SystemStyleObject = {
  display: 'inline-block',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  width: '100%',
  lineHeight: '1.3',
};
